import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useParams } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { SectionToolbar, AddButton } from "./ProfileUI";

import {
  useContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
} from "../../../api/queries/profileQueries";
import { useCheckCompleteness } from "../../../api/queries/employeeQueries";
import { getApiErrorMessage } from "../../../utils/apiError";

// Stable empty list: `contacts` is an effect dependency below.
const EMPTY = [];

export const RelationshipType = [
  "Spouse",
  "Mother",
  "Father",
  "Sibling",
  "Child",
  "Relative",
  "Friend",
  "Other",
  "Live-in Partner",
];

const EmployeeEmergencyContact = forwardRef((props, ref) => {
  const { id: employeeId } = useParams();

  const { data: contacts = EMPTY } = useContacts(employeeId);
  const createContact = useCreateContact(employeeId);
  const updateContact = useUpdateContact(employeeId);
  const deleteContact = useDeleteContact(employeeId);
  const checkCompleteness = useCheckCompleteness();

  const [localContacts, setLocalContacts] = useState([]);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const savedRows = contacts.map((c) => ({ ...c, editing: false }));
    setLocalContacts(savedRows);
  }, [contacts]);

  useImperativeHandle(ref, () => ({
    submit: async () => true,
  }));

  const showStatus = (text, type = "success") => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
  };

  const handleAddRow = () => {
    setLocalContacts((prev) => [
      ...prev,
      {
        firstName: "",
        middleName: "",
        lastName: "",
        relationship: "",
        phone: "",
        email: "",
        address: "",
        emergencyContact: false,
        editing: true,
      },
    ]);
  };

  const toggleEdit = (index) => {
    setLocalContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, editing: !c.editing } : c)),
    );
  };

  const handleInputChange = (index, name, value) => {
    setLocalContacts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handleSaveRow = async (row, index) => {
    if (
      !row.firstName?.trim() ||
      !row.lastName?.trim() ||
      !row.relationship ||
      !row.phone
    ) {
      showStatus("Missing required fields.", "danger");
      return;
    }

    setIsProcessing(true); // Trigger global overlay
    try {
      const payload = {
        employeeId,
        firstName: row.firstName,
        middleName: row.middleName,
        lastName: row.lastName,
        emergencyContact: row.emergencyContact || false,
        relationship: row.relationship,
        phone: row.phone,
        email: row.email,
        address: row.address,
      };

      // Each mutation refreshes the contact list afterwards
      if (row.id) {
        await updateContact.mutateAsync({ id: row.id, contact: payload });
        showStatus("Updated successfully!");
      } else {
        await createContact.mutateAsync(payload);
        showStatus("Added successfully!");
      }

      // Recalculates Complete/Incomplete and refreshes the employee record
      await checkCompleteness.mutateAsync(employeeId);
    } catch (err) {
      showStatus(getApiErrorMessage(err, "Save failed."), "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelRow = (index, row) => {
    if (!row.id) {
      setLocalContacts((prev) => prev.filter((_, i) => i !== index));
    } else {
      const original = contacts.find((c) => c.id === row.id);
      setLocalContacts((prev) =>
        prev.map((c, i) => (i === index ? { ...original, editing: false } : c)),
      );
    }
    setStatusMsg({ text: "", type: "" });
  };

  const handleRemoveRow = async (index, id) => {
    if (!id) {
      setLocalContacts((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm("Delete this emergency contact?")) return;

    setIsProcessing(true);
    try {
      await deleteContact.mutateAsync(id);
      await checkCompleteness.mutateAsync(employeeId);
      showStatus("Deleted successfully!");
    } catch (err) {
      showStatus(getApiErrorMessage(err, "Failed to delete record."), "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  // const isLoading = status === 'loading' || isProcessing;
  const isLoading = isProcessing;

  return (
    <div className="position-relative">
      <div>
        {/* Global Loading Overlay same with other screens */}
        {isLoading && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <SectionToolbar title="Contacts" icon="bi-people" statusMsg={statusMsg}>
          <AddButton
            onClick={handleAddRow}
            disabled={isLoading}
            label="Add contact"
          />
        </SectionToolbar>

        <div className="table-responsive profile-table-wrap contacts-table">
          <table className="table table-hover align-middle mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="py-3 ps-4 col-w-40">Emergency Contact</th>
                <th className="py-3">FULL NAME</th>
                <th className="py-3">RELATIONSHIP</th>
                <th className="py-3">PHONE</th>
                <th className="py-3">EMAIL</th>
                <th className="py-3">ADDRESS</th>
                <th className="py-3 pe-4 text-end col-w-110">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {localContacts.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center py-5 text-muted small fw-semibold"
                  >
                    No employee contacts found.
                  </td>
                </tr>
              ) : (
                localContacts.map((row, index) => (
                  <tr
                    key={row.id || `new-${index}`}
                    className={row.editing ? "bg-primary bg-opacity-10" : ""}
                  >
                    <td className="ps-4 text-center">
                      <input
                        type="checkbox"
                        className={`form-check-input shadow-none contact-check ${row.editing && !isLoading ? "is-editable" : ""}`}
                        checked={row.emergencyContact}
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "emergencyContact",
                            e.target.checked,
                          )
                        }
                        disabled={!row.editing || isLoading}
                      />
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <input
                          className={`form-control ${!row.editing ? "view-mode-input" : ""} table-field ${!row.editing ? "is-readonly" : ""}`}
                          value={row.firstName}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "firstName",
                              e.target.value,
                            )
                          }
                          placeholder="First"
                          disabled={isLoading}
                        />
                        <input
                          className={`form-control ${!row.editing ? "view-mode-input" : ""} table-field ${!row.editing ? "is-readonly" : ""}`}
                          value={row.middleName}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "middleName",
                              e.target.value,
                            )
                          }
                          placeholder="Middle"
                          disabled={isLoading}
                        />
                        <input
                          className={`form-control ${!row.editing ? "view-mode-input" : ""} table-field ${!row.editing ? "is-readonly" : ""}`}
                          value={row.lastName}
                          onChange={(e) =>
                            handleInputChange(index, "lastName", e.target.value)
                          }
                          placeholder="Last"
                          disabled={isLoading}
                        />
                      </div>
                    </td>
                    <td>
                      <select
                        className={`form-select form-select-sm ${!row.editing ? "view-mode-input" : ""} table-field ${!row.editing ? "is-readonly" : ""}`}
                        value={row.relationship}
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "relationship",
                            e.target.value,
                          )
                        }
                        disabled={!row.editing || isLoading}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        {RelationshipType.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <PhoneInput
                        className={`PhoneInput ${row.editing ? "editing-phone" : ""}`}
                        defaultCountry="PH"
                        value={row.phone}
                        onChange={(v) => handleInputChange(index, "phone", v)}
                        disabled={!row.editing || isLoading}
                      />
                    </td>
                    <td>
                      <input
                        className={`form-control ${!row.editing ? "view-mode-input" : ""} table-field ${!row.editing ? "is-readonly" : ""}`}
                        value={row.email}
                        onChange={(e) =>
                          handleInputChange(index, "email", e.target.value)
                        }
                        disabled={isLoading}
                      />
                    </td>
                    <td>
                      <input
                        className={`form-control ${!row.editing ? "view-mode-input" : ""} table-field ${!row.editing ? "is-readonly" : ""}`}
                        value={row.address}
                        onChange={(e) =>
                          handleInputChange(index, "address", e.target.value)
                        }
                        disabled={isLoading}
                      />
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {row.editing ? (
                          <>
                            <button
                              title="Save"
                              aria-label="Save"
                              className="btn btn-success btn-sm py-0 px-2 shadow-sm d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() => handleSaveRow(row, index)}
                              disabled={isLoading}
                            >
                              <i className="bi bi-check-lg"></i>
                            </button>
                            <button
                              title="Cancel"
                              aria-label="Cancel"
                              className="btn btn-outline-secondary btn-sm py-0 px-2 shadow-sm d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() =>
                                row.id
                                  ? toggleEdit(index)
                                  : handleCancelRow(index, row)
                              }
                              disabled={isLoading}
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              title="Edit"
                              aria-label="Edit"
                              className="btn btn-sm btn-outline-primary py-0 px-2 shadow-sm d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() => toggleEdit(index)}
                              disabled={isLoading}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              title="Delete"
                              aria-label="Delete"
                              className="btn btn-sm btn-outline-danger py-0 px-2 shadow-sm d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() => handleRemoveRow(index, row.id)}
                              disabled={isLoading}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

export default EmployeeEmergencyContact;
